/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function updateFreeShippingText() {
  function setShippingText() {
    const freeShippingText = document.getElementById("free-shipping-text");
    const thresholdElement = document.querySelector(".shipping-bar"); // Fixed selector

    if (!freeShippingText || !thresholdElement) return;

    // Get free shipping threshold (always in cents)
    const thresholdInCents = parseFloat(
      thresholdElement.getAttribute("threshold")
    );
    const thresholdInCurrency = thresholdInCents / 100; // Convert cents to normal currency

    if (typeof Shopify !== "undefined" && Shopify.country && Shopify.currency) {
      fetch("/cart.js")
        .then((response) => response.json())
        .then((cart) => {
          const cartTotal = cart.total_price / 100; // Convert cart total from cents

          // Currency Symbol Mapping
          const currencySymbols = {
            USD: "$",
            CAD: "$",
            EUR: "€",
            GBP: "£",
            INR: "₹",
            AUD: "$",
            JPY: "¥",
          };
          const activeCurrency = Shopify.currency.active || "USD"; // Default to USD
          const currencySymbol =
            currencySymbols[activeCurrency] || activeCurrency; // Use symbol or fallback to code

          // Format amount with just the symbol (removing currency code)
          const formatAmount = (amount) => `${currencySymbol}${amount}`;

          let correctText = "";

          if (cartTotal < thresholdInCurrency) {
            const remainingAmount = (thresholdInCurrency - cartTotal).toFixed(
              2
            );

            if (Shopify.country === "US" || Shopify.country === "CA") {
              correctText = window.themeVariables?.translations?.[
                "cart.general.free_shipping_remaining_html_us_canada"
              ]
                ? window.themeVariables.translations[
                    "cart.general.free_shipping_remaining_html_us_canada"
                  ].replace(
                    "{{ remaining_amount }}",
                    formatAmount(remainingAmount)
                  )
                : `Spend ${formatAmount(
                    remainingAmount
                  )} more to get Free Shipping & a Free Gift!`;
            } else {
              correctText = window.themeVariables?.translations?.[
                "cart.general.free_shipping_remaining_html"
              ]
                ? window.themeVariables.translations[
                    "cart.general.free_shipping_remaining_html"
                  ].replace(
                    "{{ remaining_amount }}",
                    formatAmount(remainingAmount)
                  )
                : `Spend ${formatAmount(
                    remainingAmount
                  )} more and get a Free Gift!`;
            }
          } else {
            if (Shopify.country === "US" || Shopify.country === "CA") {
              correctText = window.themeVariables?.translations?.[
                "cart.general.free_shipping_us_canada"
              ]
                ? window.themeVariables.translations[
                    "cart.general.free_shipping_us_canada"
                  ]
                : "You are eligible for Free Shipping & a Free Gift!";
            } else {
              correctText = window.themeVariables?.translations?.[
                "cart.general.free_shipping"
              ]
                ? window.themeVariables.translations[
                    "cart.general.free_shipping"
                  ]
                : "You are eligible for a Free Gift!";
            }
          }

          // Ensure correct text updates
          if (freeShippingText.textContent !== correctText) {
            freeShippingText.textContent = correctText;
          }
        })
        .catch((error) => console.error("Error fetching cart:", error));
    }
  }

  setShippingText();

  const miniCart = document.querySelector("#mini-cart");
  if (miniCart) {
    const observer = new MutationObserver(() => {
      setTimeout(setShippingText, 100);
    });

    observer.observe(miniCart, { childList: true, subtree: true });
  }

  document.addEventListener("shopify:cart:updated", setShippingText);
  document.addEventListener("shopify:section:load", setShippingText);
}

document.addEventListener("DOMContentLoaded", updateFreeShippingText);

// Route the sticky CTA through the main product CTA so third-party integrations
// (such as qikify upsells) receive the same click event as the primary button.
document.addEventListener('click', function(event) {
  const stickyAddToCartButton = event.target instanceof Element
    ? event.target.closest('#StickyAddToCart')
    : null;

  if (!stickyAddToCartButton) return;

  const productForm = stickyAddToCartButton.form;
  const mainAddToCartButton = productForm?.querySelector('#AddToCart[data-product-add-to-cart-button]');

  // Preserve the sticky button's native form submission as a fallback when the
  // corresponding primary CTA cannot be found or is unavailable.
  if (!mainAddToCartButton || mainAddToCartButton.disabled) return;

  event.preventDefault();
  mainAddToCartButton.click();
});

/* ------------------------------------------------------------------------------------------------------------
 * VARIANT PRE-ORDER LABEL
 * Shows the "Pre-order" label while a pre-order option (Theme settings > Products grid > Pre-order variant
 * options) is selected: on product cards when a color swatch changes, and on the product page media when the
 * selected variant changes.
 * ------------------------------------------------------------------------------------------------------------ */
document.addEventListener('change', (event) => {
  const swatchRadio = event.target instanceof Element
    ? event.target.closest('.product-item-meta__swatch-list .color-swatch__radio')
    : null;

  if (!swatchRadio) return;

  const preOrderLabel = swatchRadio.closest('product-item')?.querySelector('[data-kik-pre-order-label]');

  if (preOrderLabel) {
    preOrderLabel.hidden = !swatchRadio.hasAttribute('data-kik-pre-order');
  }
});

document.addEventListener('variant:changed', (event) => {
  // Product forms contain an <input name="id">, so form.id would return that input instead of the attribute
  const formId = event.target instanceof Element ? event.target.getAttribute('id') : null;
  const variant = event.detail?.variant;

  if (!formId) return;

  document.querySelectorAll(`[data-kik-pre-order-media][data-form-id="${CSS.escape(formId)}"]`).forEach((mediaLabelList) => {
    const preOrderLabel = mediaLabelList.querySelector('[data-kik-pre-order-label]');
    const preOrderVariantIds = (mediaLabelList.getAttribute('data-pre-order-variants') || '').split(',');

    if (preOrderLabel) {
      preOrderLabel.hidden = !variant || !preOrderVariantIds.includes(String(variant.id));
    }
  });
});
